import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Space,
  Result,
  Typography,
  Table,
  Timeline,
  Card,
  Divider,
  Steps,
  Tag,
  Spin,
} from "antd";
import { useStepsForm } from "sunflower-antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  EditOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { getUserApi } from "../../../services/User/UserApis";
import {
  formatAuditLabel,
  formatAuditValueNode,
} from "../../../utils/auditValueFormat";

const { Step } = Steps;
const { Title, Text, Paragraph } = Typography;

interface IModalProps {
  show: boolean;
  showModal: Function;
  content: string;
}

const AuditModal: React.FC<IModalProps> = ({
  show,
  showModal,
  content,
}): React.ReactElement => {
  const [userCache, setUserCache] = useState<{ [key: number]: any }>({});
  const [loadingUsers, setLoadingUsers] = useState<{ [key: string]: boolean }>({});

  const handleModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    total: 3,
  });

  // Fetch user information by ID
  const fetchUser = async (userId: number) => {
    if (userCache[userId]) {
      return userCache[userId];
    }

    if (loadingUsers[userId]) {
      return null;
    }

    setLoadingUsers((prev) => ({ ...prev, [userId]: true }));

    try {
      const response = await getUserApi(String(userId));
      if (response.success && response.data) {
        const userData = response.data;
        setUserCache((prev) => ({ ...prev, [userId]: userData }));
        setLoadingUsers((prev) => ({ ...prev, [userId]: false }));
        return userData;
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
    }

    setLoadingUsers((prev) => ({ ...prev, [userId]: false }));
    return null;
  };

  // Check if a key represents a user ID field
  const isUserIdField = (key: string): boolean => {
    const userIdPattern = /^(confirmed|created|updated|modified|approved|rejected)By$/i;
    return userIdPattern.test(key);
  };

  // Format user ID to display user name
  const formatUserValue = (key: string, value: any) => {
    if (isUserIdField(key) && typeof value === "number" && value > 0) {
      const user = userCache[value];
      if (user) {
        return `${user.Name} ${user.Surname} (ID: ${value})`;
      }
      if (loadingUsers[value]) {
        return <Spin size="small" />;
      }
      // Trigger fetch
      fetchUser(value);
      return value; // Show ID while loading
    }
    return value;
  };

  // Prefetch user data when content changes
  useEffect(() => {
    if (content && show) {
      try {
        const auditData = typeof content === "string" ? JSON.parse(content) : content;
        if (typeof auditData === "object" && !Array.isArray(auditData)) {
          Object.entries(auditData).forEach(([key, value]) => {
            if (isUserIdField(key) && typeof value === "number" && value > 0) {
              fetchUser(value);
            }
          });
        }
      } catch (e) {
        // Parsing error, ignore
      }
    }
  }, [content, show]);

  // Get icon based on action type
  const getActionIcon = (action: string) => {
    const actionLower = (action || "").toLowerCase();
    if (actionLower.includes("create") || actionLower.includes("add"))
      return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
    if (actionLower.includes("delete") || actionLower.includes("remove"))
      return <ExclamationCircleOutlined style={{ color: "#f5222d" }} />;
    if (actionLower.includes("update") || actionLower.includes("edit"))
      return <EditOutlined style={{ color: "#1890ff" }} />;
    if (actionLower.includes("view") || actionLower.includes("read"))
      return <FileTextOutlined style={{ color: "#722ed1" }} />;
    return <ToolOutlined style={{ color: "#faad14" }} />;
  };

  // Get tag color based on action type
  const getActionColor = (action: string) => {
    const actionLower = (action || "").toLowerCase();
    if (actionLower.includes("create") || actionLower.includes("add"))
      return "success";
    if (actionLower.includes("delete") || actionLower.includes("remove"))
      return "error";
    if (actionLower.includes("update") || actionLower.includes("edit"))
      return "processing";
    if (actionLower.includes("view") || actionLower.includes("read"))
      return "purple";
    return "warning";
  };

  // Parse content if it's a JSON string
  const renderAuditContent = () => {
    try {
      // Attempt to parse if content is a JSON string
      const auditData =
        typeof content === "string" ? JSON.parse(content) : content;

      if (Array.isArray(auditData)) {
        // If it's an array, render as timeline
        return (
          <Card
            className="audit-card"
            bordered={false}
            style={{ backgroundColor: "#f8f9fa" }}
          >
            <Title level={4}>
              <HistoryOutlined style={{ color: "#f04f05" }} /> Audit History
            </Title>
            <Timeline mode="left">
              {auditData.map((item, index) => {
                const action = item.action || item.event || "N/A";
                return (
                  <Timeline.Item
                    key={index}
                    dot={getActionIcon(action)}
                    color={getActionColor(action)}
                  >
                    <Card
                      size="small"
                      style={{
                        marginBottom: 10,
                        borderLeft: `3px solid ${
                          item.action ? getActionColor(action) : "#d9d9d9"
                        }`,
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.09)",
                      }}
                    >
                      <p>
                        <ClockCircleOutlined
                          style={{ color: "#f04f05", marginRight: 8 }}
                        />
                        <strong>Date:</strong>{" "}
                        {item.date || item.timestamp || "N/A"}
                      </p>
                      <p>
                        <Tag color={getActionColor(action)}>{action}</Tag>
                      </p>
                      <p>
                        <UserOutlined
                          style={{ color: "#722ed1", marginRight: 8 }}
                        />
                        <strong>User:</strong>{" "}
                        {item.user || item.userName || "System"}
                      </p>
                      {item.details && (
                        <p>
                          <InfoCircleOutlined
                            style={{ color: "#faad14", marginRight: 8 }}
                          />
                          <strong>Details:</strong> {item.details}
                        </p>
                      )}
                    </Card>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </Card>
        );
      } else {
        // If it's an object, render as a card with details
        return (
          <Card
            className="audit-card"
            bordered={false}
            style={{ backgroundColor: "#f8f9fa" }}
          >
            <Title level={4}>
              <InfoCircleOutlined style={{ color: "#f04f05" }} /> Audit
              Information
            </Title>
            <Divider style={{ borderColor: "#f04f05" }} />
            {Object.entries(auditData).map(([key, value]) => {
              const displayValue = isUserIdField(key)
                ? formatUserValue(key, value)
                : value;

              const formattedValue = React.isValidElement(displayValue)
                ? displayValue
                : formatAuditValueNode(displayValue);

              return (
                <div key={key} style={{ marginBottom: 16 }}>
                  <Text strong style={{ color: "#f04f05" }}>
                    {formatAuditLabel(key)}:{" "}
                  </Text>
                  {typeof formattedValue === "string" ? (
                    <Text>{formattedValue}</Text>
                  ) : (
                    formattedValue
                  )}
                </div>
              );
            })}
          </Card>
        );
      }
    } catch (e) {
      // If parsing fails, display as formatted text
      return (
        <Card
          className="audit-card"
          bordered={false}
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <Title level={4}>
            <FileTextOutlined style={{ color: "#f04f05" }} /> Audit Information
          </Title>
          <Divider style={{ borderColor: "#f04f05" }} />
          <Paragraph>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                backgroundColor: "#f0f5ff",
                padding: 16,
                borderRadius: 4,
                maxHeight: "400px",
                overflowY: "auto",
                borderLeft: "4px solid #f04f05",
              }}
            >
              {content}
            </pre>
          </Paragraph>
        </Card>
      );
    }
  };

  const display: any = {
    0: renderAuditContent(),
  };

  return (
    <Modal
      title={
        <div style={{ fontSize: 18, fontWeight: "bold" }}>
          <HistoryOutlined style={{ marginRight: 8, color: "#f04f05" }} /> Audit
          Trail
        </div>
      }
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={900}
      footer={[
        <Button
          key="close"
          type="primary"
          onClick={handleModal}
          icon={<ClockCircleOutlined />}
        >
          Close
        </Button>,
      ]}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: "24px" }}
    >
      {/* Steps Form */}
      <div>
        {current === 1 && <></>}
        <div style={{ marginTop: 20 }}>
          <Form {...formProps} style={{ maxWidth: 900 }}>
            {display[current]}
          </Form>

          {current === 1 && (
            <Result status="success" title="Successfully submitted">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  block
                  size="large"
                  type="primary"
                  onClick={handleModal}
                  icon={<CheckCircleOutlined />}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 2 && (
            <Result status="error" title="Error occurred, please try again.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  block
                  size="large"
                  type="primary"
                  danger
                  onClick={handleModal}
                  icon={<ExclamationCircleOutlined />}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AuditModal;
